/**
 * AuditLogTable Component
 * Displays a table of audit log entries with pagination
 */

import { AuditLogEntry } from './AuditLogEntry';
import { cardStyles, tableStyles } from '../../lib/styles';
import type { AuditLogEntry as AuditLogEntryType, Pagination } from '@leal-crm/shared';

interface AuditLogTableProps {
  logs: AuditLogEntryType[];
  pagination: Pagination;
  isLoading?: boolean;
  onViewDetails?: (entry: AuditLogEntryType) => void;
  onPageChange?: (page: number) => void;
}

export function AuditLogTable({
  logs,
  pagination,
  isLoading = false,
  onViewDetails,
  onPageChange,
}: AuditLogTableProps) {
  if (isLoading) {
    return (
      <div className={cardStyles.container}>
        <div className={cardStyles.content}>
          <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          </div>
        </div>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className={cardStyles.container}>
        <div className={cardStyles.content}>
          <div className="py-8 text-center">
            <div className="text-2xl mb-2">!</div>
            <p className="text-xs text-gray-500">No se encontraron registros de auditoria</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={tableStyles.container}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={tableStyles.headerRow}>
              <th className={tableStyles.headerCell}>Fecha/Hora</th>
              <th className={tableStyles.headerCell}>Usuario</th>
              <th className={tableStyles.headerCell}>Evento</th>
              <th className={tableStyles.headerCell}>Entidad</th>
              <th className={tableStyles.headerCell}>IP</th>
              <th className={`${tableStyles.headerCell} text-right`}>Detalles</th>
            </tr>
          </thead>
          <tbody className={tableStyles.body}>
            {logs.map((entry) => (
              <AuditLogEntry
                key={entry.id}
                entry={entry}
                onViewDetails={onViewDetails}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination - responsive layout */}
      {pagination.totalPages > 1 && (
        <div className={cardStyles.footer}>
          {/* Mobile pagination */}
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={!pagination.hasPrevPage}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="flex items-center text-xs text-gray-700">
              {pagination.page} / {pagination.totalPages}
            </span>
            <button
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={!pagination.hasNextPage}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
          {/* Desktop pagination */}
          <div className="hidden sm:flex sm:items-center sm:justify-between">
            <div className="text-xs text-gray-500">
              Mostrando {(pagination.page - 1) * pagination.pageSize + 1} a{' '}
              {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} de{' '}
              {pagination.totalItems} registros
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => onPageChange?.(pagination.page - 1)}
                disabled={!pagination.hasPrevPage}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="flex items-center px-2 text-xs text-gray-700">
                Pagina {pagination.page} de {pagination.totalPages}
              </span>
              <button
                onClick={() => onPageChange?.(pagination.page + 1)}
                disabled={!pagination.hasNextPage}
                className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

